'use client'

import React, { useEffect, useRef, useState, PropsWithChildren } from 'react'
import { Client, IMessage, ActivationState } from '@stomp/stompjs'
import { useCookies } from 'next-client-cookies'
import { WebSocketContext } from './web-socket-context'
import { useMeStore } from '@/store'
import config from '@/config'

const WebSocketProvider = ({ children }: PropsWithChildren) => {
  const clientRef = useRef<Client | null>(null)
  const subscriptionsRef = useRef<Map<string, any>>(new Map())
  const cookies = useCookies()

  const { meState } = useMeStore()
  const userId = meState.response?.id
  const token = cookies.get('accessToken')
  const tokenRef = useRef(token)
  tokenRef.current = token

  const [userNotifications, setUserNotifications] = useState<any[]>([])
  const [publicNotifications, setPublicNotifications] = useState<any[]>([])
  const [chats, setChats] = useState<any[]>([])
  const [userStatuses, setUserStatuses] = useState<Record<string, boolean>>({})
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let isMounted = true
    const subscriptions = subscriptionsRef.current

    const initializeWebSocket = async () => {
      const SockJS = (await import('sockjs-client')).default

      if (!isMounted) {
        return
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log(`[WebSocket] Initializing connection... (${tokenRef.current ? 'authenticated' : 'anonymous'})`)
      }

      const connectHeaders: Record<string, string> = {}
      if (tokenRef.current) {
        connectHeaders['Authorization'] = `Bearer ${tokenRef.current}`
      }

      const client = new Client({
        webSocketFactory: () => {
          const sockjs = new SockJS(`${config.API_BASE_URL}/ws`, null, {
            transports: ['websocket'],
            timeout: 10000
          })
          return sockjs as any
        },
        connectHeaders,
        debug: (str: string) => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[STOMP]', str)
          }
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000
      })

      client.onConnect = () => {
        if (!isMounted) {
          return
        }
        if (process.env.NODE_ENV !== 'production') {
          console.log('✅ WebSocket connected')
        }
        setIsConnected(true)
        subscriptions.clear()

        try {
          const publicSub = client.subscribe('/topic/notifications', (msg: IMessage) => {
            try {
              setPublicNotifications(prev => [...prev, JSON.parse(msg.body)])
            } catch (error) {
              console.error('[WebSocket] Error parsing public notification:', error)
            }
          })
          subscriptions.set('/topic/notifications', publicSub)
        } catch (error) {
          console.error('[WebSocket] Error subscribing to public notifications:', error)
        }

        try {
          const userStatusSub = client.subscribe('/topic/user-status', (msg: IMessage) => {
            try {
              const { userId: statusUserId, online } = JSON.parse(msg.body)
              setUserStatuses(prev => ({ ...prev, [statusUserId]: online }))
            } catch (error) {
              console.error('[WebSocket] Error parsing user status:', error)
            }
          })
          subscriptions.set('/topic/user-status', userStatusSub)
        } catch (error) {
          console.error('[WebSocket] Error subscribing to user status:', error)
        }

        if (userId) {
          try {
            const userNotifSub = client.subscribe('/user/queue/notifications', (msg: IMessage) => {
              try {
                setUserNotifications(prev => [...prev, JSON.parse(msg.body)])
              } catch (error) {
                console.error('[WebSocket] Error parsing user notification:', error)
              }
            })
            subscriptions.set('/user/queue/notifications', userNotifSub)

            const chatSub = client.subscribe('/user/queue/chat', (msg: IMessage) => {
              try {
                setChats(prev => [...prev, JSON.parse(msg.body)])
              } catch (error) {
                console.error('[WebSocket] Error parsing chat message:', error)
              }
            })
            subscriptions.set('/user/queue/chat', chatSub)
          } catch (error) {
            console.error('[WebSocket] Error subscribing to user-specific topics:', error)
          }
        }
      }

      client.onStompError = frame => {
        if (!isMounted) {
          return
        }
        console.error('❌ WebSocket STOMP error:', frame.headers['message'])
        if (process.env.NODE_ENV !== 'production') {
          console.error('[WebSocket] Details:', frame.body)
        }
        setIsConnected(false)
      }

      client.onWebSocketError = event => {
        if (!isMounted) {
          return
        }
        console.error('❌ WebSocket error:', event)
        setIsConnected(false)
      }

      client.onDisconnect = () => {
        if (!isMounted) {
          return
        }
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔌 WebSocket disconnected')
        }
        setIsConnected(false)
        subscriptions.clear()
      }

      client.onWebSocketClose = event => {
        if (!isMounted) {
          return
        }
        if (event.code !== 1000 && process.env.NODE_ENV !== 'production') {
          console.log(`🔌 WebSocket closed: ${event.code} - ${event.reason || 'Unknown reason'}`)
        }
        setIsConnected(false)
      }

      try {
        client.activate()
        clientRef.current = client
      } catch (error) {
        console.error('[WebSocket] Activation error:', error)
      }
    }

    void initializeWebSocket()

    return () => {
      isMounted = false

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Cleaning up connection...')
      }

      if (clientRef.current?.state === ActivationState.ACTIVE) {
        subscriptions.forEach(subscription => {
          try {
            subscription.unsubscribe()
          } catch (error) {
            console.error('[WebSocket] Error unsubscribing:', error)
          }
        })
        subscriptions.clear()
      }

      if (clientRef.current) {
        void clientRef.current.deactivate()
      }
    }
  }, [userId])

  const subscribe = (destination: string, onMessage: (msg: any) => void) => {
    if (!clientRef.current || clientRef.current.state !== ActivationState.ACTIVE) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[WebSocket] Cannot subscribe - client not connected')
      }
      return
    }

    try {
      const subscription = clientRef.current.subscribe(destination, (msg: IMessage) => {
        try {
          onMessage(JSON.parse(msg.body))
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error)
        }
      })
      subscriptionsRef.current.set(destination, subscription)

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Subscribed to:', destination)
      }
    } catch (error) {
      console.error('[WebSocket] Subscribe error:', error)
    }
  }

  const sendMessage = (destination: string, body: any) => {
    if (!clientRef.current || clientRef.current.state !== ActivationState.ACTIVE) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[WebSocket] Cannot send message - client not connected')
      }
      return
    }

    try {
      clientRef.current.publish({
        destination,
        body: JSON.stringify(body)
      })

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Message sent to:', destination)
      }
    } catch (error) {
      console.error('[WebSocket] Send message error:', error)
    }
  }

  return (
    <WebSocketContext.Provider
      value={{ userNotifications, publicNotifications, chats, userStatuses, isConnected, subscribe, sendMessage }}>
      {children}
    </WebSocketContext.Provider>
  )
}

export default WebSocketProvider
